// components/ProductAdvice.js
'use client'
import { useState, useEffect } from 'react';
import Link from 'next/link';

const ProductAdvice = ({ product }) => {
    const [advice, setAdvice] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Функция для получения советов
    const fetchAdviceForProduct = async (categories, tags) => {
        const WP_URL = process.env.NEXT_PUBLIC_WP_URL;

        try {
            let allAdvice = [];

            // Получаем советы по категориям
            for (const category of categories) {
                try {
                    const response = await fetch(`${WP_URL}/wp-json/product-advice/v1/by-category/${category.slug}`);
                    if (response.ok) {
                        const categoryAdvice = await response.json();
                        allAdvice = [...allAdvice, ...categoryAdvice];
                    }
                } catch (error) {
                    console.warn(`Ошибка при получении советов для категории ${category.slug}:`, error);
                }
            }

            // Получаем советы по меткам
            for (const tag of tags) {
                try {
                    const response = await fetch(`${WP_URL}/wp-json/product-advice/v1/by-tag/${tag.slug}`);
                    if (response.ok) {
                        const tagAdvice = await response.json();
                        allAdvice = [...allAdvice, ...tagAdvice];
                    }
                } catch (error) {
                    console.warn(`Ошибка при получении советов для метки ${tag.slug}:`, error);
                }
            }

            // Убираем дубликаты по ID
            const uniqueAdvice = allAdvice.filter((advice, index, self) =>
                index === self.findIndex(a => a.id === advice.id)
            );

            // Возвращаем максимум 3 совета
            return uniqueAdvice.slice(0, 3);
        } catch (error) {
            console.error('Ошибка при получении советов:', error);
            throw error;
        }
    };

    useEffect(() => {
        const loadAdvice = async () => {
            if (!product) return;

            setLoading(true);
            setError(null);

            try {
                const categories = product.productCategories?.nodes || [];
                const tags = product.productTags?.nodes || [];

                if (categories.length === 0 && tags.length === 0) {
                    setAdvice([]);
                    return;
                }

                const adviceData = await fetchAdviceForProduct(categories, tags);
                setAdvice(adviceData || []);
            } catch (err) {
                setError('Не удалось загрузить советы');
                setAdvice([]);
            } finally {
                setLoading(false);
            }
        };

        // Задержка для имитации загрузки после основного контента
        const timer = setTimeout(loadAdvice, 500);
        return () => clearTimeout(timer);
    }, [product]);

    // Прелоадер карточек
    const SkeletonCard = () => (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden animate-pulse">
            <div className="h-40 bg-gray-200"></div>
            <div className="p-4">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3 mb-3"></div>
                <div className="h-6 bg-gray-200 rounded w-20"></div>
            </div>
        </div>
    );

    // Если нет данных о товаре или нет советов, не показываем компонент
    if (!product || (!loading && !error && advice.length === 0)) return null;

    return (
        <div id="product-advice-section" className="max-w-7xl mx-auto mt-16">
            {/* Заголовок секции */}
            <div className="mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    Советы по приему
                </h2>
                <p className="text-gray-600">
                    Рекомендации по правильному применению витаминов и БАДов
                </p>
            </div>

            {loading && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2].map((i) => (
                        <SkeletonCard key={i} />
                    ))}
                </div>
            )}

            {error && (
                <div className="text-center py-12">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-6 inline-block">
                        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <p className="text-red-700 font-medium">{error}</p>
                    </div>
                </div>
            )}

            {!loading && !error && advice.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {advice.map((item) => (
                        <Link
                            key={item.id}
                            href={`/product-advice/${item.slug}`}
                            className="block"
                        >
                            <article className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:shadow-lg hover:border-gray-200 transition-all duration-300 cursor-pointer group h-full">
                                {/* Изображение или иконка */}
                                <div className="relative h-40 bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
                                    {item.featured_image ? (
                                        <img
                                            src={item.featured_image.medium || item.featured_image.full}
                                            alt={item.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center w-full h-full">
                                            <svg className="w-12 h-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                            </svg>
                                        </div>
                                    )}

                                    {/* Бейдж с категорией */}
                                    {(item.category_name || item.tag_name) && (
                                        <div className="absolute top-3 left-3">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                {item.category_name || item.tag_name}
                                            </span>
                                        </div>
                                    )}

                                    {/* Иконка просмотров */}
                                    <div className="absolute top-3 right-3">
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-black bg-opacity-50 text-white">
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                            </svg>
                                            {item.views || 0}
                                        </span>
                                    </div>
                                </div>

                                {/* Контент карточки */}
                                <div className="p-5 flex flex-col h-[calc(100%-10rem)]">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-green-600 transition-colors">
                                        {item.title}
                                    </h3>

                                    <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed flex-grow">
                                        {item.excerpt || 'Полезные рекомендации по правильному применению витаминов для достижения максимального эффекта.'}
                                    </p>

                                    {/* Кнопка "Читать" */}
                                    <div className="flex items-center justify-between mt-auto">
                                        <span className="text-xs text-gray-500">
                                            Совет по приему
                                        </span>
                                        <span className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-green-600 bg-green-50 group-hover:bg-green-100 transition-colors">
                                            Читать
                                            <svg className="ml-1 w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                            </article>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProductAdvice;